/**
 * Phase F11 FE-2b2 — periodic screen-capture loop.
 *
 * Symmetric to :func:`useWebcamStillCapture` but consumes a
 * ``MediaStream`` produced by :class:`ScreenCaptureGate` (which had
 * to call ``navigator.mediaDevices.getDisplayMedia`` from inside a
 * click handler — browsers refuse the API outside a user gesture).
 *
 * The hook does NOT call ``getDisplayMedia``: that would crash with
 * ``InvalidStateError`` on every modern browser. The gate component
 * ships the stream up via prop; the hook owns the tick loop + the
 * stream's lifetime from that point on.
 *
 * Tick cadence:
 *   Default 60 s — slower than webcam stills because each frame is
 *   ~10x larger (full desktop @ 1920x1080) and the F12 review surface
 *   doesn't need 30 s granularity for screen-share evidence (a
 *   shared-screen cheat takes minutes, not seconds, to set up).
 *   Clamped to a 15 s minimum.
 *
 * Failure semantics:
 *   * Stream ends mid-attempt (user clicks the browser's "Stop
 *     sharing" button, or the OS revokes the surface) → record a
 *     ``consent_denied`` event with
 *     ``{ reason: "screen_loop_track_ended" }`` so the F12 review
 *     surface can render an "applicant stopped sharing screen at
 *     T+12m" marker. Stop ticking.
 *   * Per-tick capture or upload failure → swallow + report on
 *     ``lastError``; next tick still fires. One network hiccup
 *     doesn't kill the loop.
 *
 * Privacy: same posture as the webcam loop — bytes are captured to
 * an offscreen canvas and uploaded directly. The browser's own
 * "you are sharing" indicator stays on for the entire attempt
 * (browser policy); this hook never re-prompts the applicant.
 */
import * as React from "react";

import type { ProctorClient } from "@/lib/proctor-client";

export interface UseScreenCaptureLoopArgs {
  client: ProctorClient;
  /**
   * The display-media ``MediaStream`` produced by the gate. ``null``
   * → no-op (the gate has not yet been satisfied, or the loop has
   * been torn down). When the value flips to a fresh stream the
   * hook resumes ticking from t=cadence.
   */
  stream: MediaStream | null;
  intervalMs?: number;
}

export interface UseScreenCaptureLoopResult {
  capturesEnqueued: number;
  capturesUploaded: number;
  lastError: ScreenCaptureError | null;
}

export type ScreenCaptureErrorKind =
  | "stream_ended"
  | "capture_readback_failed"
  | "upload_failed"
  | "transient_error";

export interface ScreenCaptureError {
  kind: ScreenCaptureErrorKind;
  message: string;
}

const _MIN_INTERVAL_MS = 15_000;
const _DEFAULT_INTERVAL_MS = 60_000;
const _MAX_CAPTURE_WIDTH = 1920;
const _MAX_CAPTURE_HEIGHT = 1080;
const _JPEG_QUALITY = 0.6;

export function useScreenCaptureLoop({
  client,
  stream,
  intervalMs = _DEFAULT_INTERVAL_MS,
}: UseScreenCaptureLoopArgs): UseScreenCaptureLoopResult {
  const [capturesEnqueued, setCapturesEnqueued] = React.useState(0);
  const [capturesUploaded, setCapturesUploaded] = React.useState(0);
  const [lastError, setLastError] = React.useState<ScreenCaptureError | null>(null);

  const cadence = Math.max(_MIN_INTERVAL_MS, intervalMs);

  React.useEffect(() => {
    if (!stream) return;
    let cancelled = false;

    const offscreen = document.createElement("canvas");
    const ctx = offscreen.getContext("2d");
    if (!ctx) {
      setLastError({
        kind: "capture_readback_failed",
        message: "Browser refused 2D canvas context for proctor screen capture.",
      });
      return;
    }

    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.autoplay = true;
    video.srcObject = stream;
    video.play().catch(() => undefined);

    // Resize the offscreen canvas to match the actual stream once
    // metadata is available. Cap at 1920x1080 to keep payload sane
    // on 4K displays.
    function syncCanvasSize(): void {
      const w = video.videoWidth || _MAX_CAPTURE_WIDTH;
      const h = video.videoHeight || _MAX_CAPTURE_HEIGHT;
      const scale = Math.min(1, _MAX_CAPTURE_WIDTH / w, _MAX_CAPTURE_HEIGHT / h);
      offscreen.width = Math.floor(w * scale);
      offscreen.height = Math.floor(h * scale);
    }
    video.addEventListener("loadedmetadata", syncCanvasSize, { once: true });

    async function captureOnce(): Promise<void> {
      if (cancelled) return;
      if (video.readyState < 2) return;
      if (offscreen.width === 0 || offscreen.height === 0) syncCanvasSize();
      try {
        if (!ctx) return;
        ctx.drawImage(video, 0, 0, offscreen.width, offscreen.height);
      } catch (err) {
        setLastError({
          kind: "capture_readback_failed",
          message: errorMessage(err),
        });
        return;
      }
      const blob = await new Promise<Blob | null>((resolve) =>
        offscreen.toBlob((b) => resolve(b), "image/jpeg", _JPEG_QUALITY),
      );
      if (!blob || cancelled) return;
      setCapturesEnqueued((n) => n + 1);
      try {
        await client.uploadArtifact({
          blob,
          kind: "screen_capture",
          mimeType: "image/jpeg",
          capturedMetadata: {
            frame_width: offscreen.width,
            frame_height: offscreen.height,
          },
        });
        if (!cancelled) {
          setCapturesUploaded((n) => n + 1);
          setLastError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setLastError({ kind: "upload_failed", message: errorMessage(err) });
      }
    }

    const tick = setInterval(() => {
      void captureOnce();
    }, cadence);

    // Surface the user clicking the browser-native "Stop sharing"
    // chip as a `consent_denied` lifecycle event. We don't kill the
    // attempt — the runner UI will surface a visible reminder banner
    // (handled at the section level) and the F12 ML pass treats
    // mid-attempt revocation as a strong signal.
    const onTrackEnded = (): void => {
      if (cancelled) return;
      void client.recordEvent("consent_denied", {
        reason: "screen_loop_track_ended",
      });
      setLastError({
        kind: "stream_ended",
        message: "Screen sharing was stopped.",
      });
    };
    for (const track of stream.getVideoTracks()) {
      track.addEventListener("ended", onTrackEnded);
    }

    return () => {
      cancelled = true;
      clearInterval(tick);
      for (const track of stream.getVideoTracks()) {
        track.removeEventListener("ended", onTrackEnded);
      }
      video.srcObject = null;
      // We do NOT stop the stream tracks here — the parent
      // (ScreenCaptureGate's owner) holds the canonical reference
      // and decides when the screen share ends. The hook is one of
      // potentially many subscribers in future revisions.
    };
  }, [stream, cadence, client]);

  return { capturesEnqueued, capturesUploaded, lastError };
}

function errorMessage(err: unknown): string {
  if (err === null || err === undefined) return "Unknown error.";
  if (typeof err === "string") return err;
  if (typeof err === "object" && "message" in err) {
    return String((err as { message: unknown }).message);
  }
  return "Unknown error.";
}
